
export interface SearchSelectItems {
    name: string,
    value: string;
}
export interface ProductCategory extends SearchSelectItems {
    icon: string
}

export interface ItemClassifications {
    productCategories: Array<ProductCategory>;

    conditionOptions: Array<SearchSelectItems>;
}
export interface BsUserLogin {
    email: string,
    password: string;
    rememberMe: boolean;
}
export interface BsUserLoggedIn {
    firstName: string,
    lastName: string,
    username: string,
    email: string,
    birthday: number,
    readyToTrade: boolean;
}
export interface BsUserRegistration extends BsUserLoggedIn {
    password: string;
}

export interface BsUser extends BsUserRegistration {
    banned: boolean,
    password: string,
    created: number,
    lastResetDate: number,
    readyToTrade: boolean,
    addresses: Array<Address>
    //to do: add a pendingReset: string, where string is a 6-digit token generated and sent to email with a link
    pendingReset: string;
    verified: boolean;
    role: "user" | "admin"
}

export interface UserListing { // what the user will actually submit
    /** @deprecated: Titles are no longer used */
    title: string;
    tagsAliases: Array<string>;
    price: number;
    images: Array<string>;
    primaryImage: number;
}

export interface Listing<T = string> extends UserListing {
    userId: T;
    state: "draft" | "published" | "processing" | "completed" | "removed"; // processing means we (BackyardSwap) are actively arranging the exchange after the parties agreed
    views: number;
}

export interface BsListing<T = string> extends Listing<T> {
    dateSubmitted: number;
    dateModified: number;
    description: string;
}


export interface BsFile<T = string> {
    userId: T,
    path: string,
    reducedPath: string
    size: number,
    type: 'image' | 'proofdocument' | 'feedbackfile';
    time: number;
}
export enum TransactionType {
    Accepted,
    CounterOffer,
    Overridden,
    Declined
}
export interface BarteringTransaction {
    owner: boolean
    type: TransactionType; // If accepted or declined, the official conversation is finished
    items: Array<string>; // a counter offer based on items by either individual. the owner can only remove items, the proposee can add or remove items
    priceCounter: number; //a counter offer based on price by either individual
    date: number;
}
type OptionalKeys = 'priceCounter' | 'items';

export type ReducedBarteringTransaction = {
    [K in keyof BarteringTransaction]: K extends OptionalKeys ? BarteringTransaction[K] | undefined : BarteringTransaction[K];
};

export interface BarteringDocument<T = string> {
    userId: T; // user that was interested in the item
    listingId: T; // initial item being barterered
    initialPrice: number // initial price the item was bartered at
    date: number; // date item was marked as interested
    transactions: Array<BarteringTransaction>
    state: "active" | "inactive" | "completed"
}
export interface ExtendedTransaction extends BarteringTransaction {
    previousItems: Array<string>
    previousPrice: number;
}
export interface ExtendedBarteringDocument extends WithStringId<BarteringDocument> {
    buyerInventory: Array<WithStringId<BsListing>>;
    listing: WithStringId<BsListing>;
    transactions: Array<ExtendedTransaction>,
    responseNeeded: boolean,
    outgoing: boolean
}
export interface ProductTag<T = string> {
    title: string;
    icon?: string;
    type: "classification" | "brand" | "age" | "room" | "condition" | "model" | "internal" | string,
    alias: string, // Must be a really short version of the title. This will frequently appear in URLs
    hidden: boolean;
    description?: string;
    children: Array<T> // by adding this product tag, items in linked product tags will be forcibly added
}

export interface TagType {
    id: string,
    title: string,
    required: boolean;
    allowedChildren: Array<string>,
    removeable?: boolean
}

export interface Feedback<T = string> {
    userId?: T;
    ip: string;
    type: 'bugreport' | 'suggestion'
    time: number;
    url: string;
    read: boolean;
    message: string;
}
export interface Address {
    firstName: string;
    lastName: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
}
export interface WorkOrderPrice {
    subTotal: number;
    taxRate: number;
    taxPrice: number;
    labor: number;
    total: number;
    earnings: number;
}
export interface WorkOrder<T = string> {
    buyerId: T,
    sellerId: T
    listingId: T,
    dateCreated: number,
    dateUpdated: number,
    sellerDeliveredDate?: number,
    buyerDeliveredDate?: number,
    buyerItems: Array<string>, // for now should only contain 1 item
    sellerItems: Array<string>,
    buyerPrice: WorkOrderPrice, // for now should be $0. if $0, workorders page shouldnt ask for payment.
    sellerPrice: WorkOrderPrice,
    buyerShipping?: Address,
    sellerShipping?: Address,
    buyerPayCollected: boolean;
    sellerPayCollected: boolean;
    buyerItemsCollected: boolean;
    sellerItemsCollected: boolean;
    complete: boolean;
}

export interface Notification<T = string> {
    workOrder: T,
    buyer: T,
    buyerAcknowledged: boolean;
    seller: T,
    sellerAcknowledged: boolean;
    time: number,
    items?: Array<string>
    message: string
}

export interface Variables {
    appStoreLink: string,
    screenshot1: string,
    screenshot2: string,
    screenshot3: string
}

export type WithStringId<T> = T & { _id: string };